import { SlateNode } from '@src/components/sco/RichMarkdownInput'
import { EditorElementTypes } from '@src/components/sco/RichMarkdownInput/components/Element'
import { LinkifyIt } from 'linkify-it'
import { Descendant, Editor, Node, Path, Range, Text, Transforms } from 'slate'

/**
 * One linkifier for the editor: paste, typing (space / Enter) and the markdown handed to
 * consumers all find links with findLinks, so a URL is linked the same way wherever it
 * comes from.
 */

const linkify = new LinkifyIt({ fuzzyLink: true, fuzzyEmail: true, fuzzyIP: false })
  .add('ftp:', null)
  .add('//', null)

// Text inside these is never linkified
const SKIPPED_ELEMENT_TYPES: string[] = [
  EditorElementTypes.LINK,
  EditorElementTypes.MENTION,
  EditorElementTypes.CODE_BLOCK,
]

// Inline `code` is a text mark, not an element
const isCode = (leaf: Text) => Boolean((leaf as Text & { code?: boolean }).code)

export type FoundLink = { start: number; end: number; url: string }

/**
 * http(s) and mailto links, email addresses, and www. hosts (linked as https). Bare domains
 * like "Startups.com" stay text: in prose they are far more often a name than a link.
 */
export function findLinks(text: string): FoundLink[] {
  if (!text || !linkify.test(text)) {
    return []
  }
  return (linkify.match(text) ?? []).flatMap(match => {
    if (match.schema === '' && !/^www\./i.test(match.raw)) {
      return []
    }
    const url = match.schema === '' ? `https://${match.raw}` : match.url
    return [{ start: match.index, end: match.lastIndex, url }]
  })
}

/** True when the whole string (ignoring surrounding whitespace) is a single link */
export function isUrl(text: string) {
  const trimmed = text?.trim() ?? ''
  const links = findLinks(trimmed)
  return links.length === 1 && links[0].start === 0 && links[0].end === trimmed.length
}

const linkElement = (url: string, children: Descendant[]) =>
  ({ type: EditorElementTypes.LINK, url, children }) as SlateNode

/**
 * Returns a copy of the nodes with every link found in plain text wrapped in a link node.
 * Used on the markdown output, so a URL is a link even if the user never typed a space or
 * Enter after it (e.g. the last word before sending).
 */
export function linkifyNodes(nodes: Descendant[]): Descendant[] {
  return nodes.map(node => {
    if (Text.isText(node) || SKIPPED_ELEMENT_TYPES.includes((node as SlateNode).type)) {
      return node
    }
    const children = (node as SlateNode).children
    if (!Array.isArray(children)) {
      return node
    }
    return {
      ...node,
      children: children.flatMap(child => (Text.isText(child) ? linkifyText(child) : linkifyNodes([child]))),
    }
  })
}

function linkifyText(leaf: Text): Descendant[] {
  const links = isCode(leaf) ? [] : findLinks(leaf.text)
  if (links.length === 0) {
    return [leaf]
  }

  const nodes: Descendant[] = []
  let position = 0
  for (const { start, end, url } of links) {
    nodes.push({ ...leaf, text: leaf.text.slice(position, start) })
    nodes.push(linkElement(url, [{ ...leaf, text: leaf.text.slice(start, end) }]))
    position = end
  }
  nodes.push({ ...leaf, text: leaf.text.slice(position) })
  return nodes
}

const isSkipped = (editor: Editor, path: Path) =>
  isCode(Node.leaf(editor, path)) ||
  Editor.above(editor, {
    at: path,
    match: n => !Editor.isEditor(n) && SKIPPED_ELEMENT_TYPES.includes((n as SlateNode).type),
  })

/** Wraps the links in the text leaf at `path` that lie within [from, to] in link nodes */
function linkifyLeaf(editor: Editor, path: Path, from = 0, to = Infinity) {
  if (isSkipped(editor, path)) {
    return
  }
  const links = findLinks(Node.leaf(editor, path).text).filter(link => link.start >= from && link.end <= to)

  // Last first: wrapping splits the leaf, and the text before the link keeps `path`
  for (const { start, end, url } of links.reverse()) {
    Transforms.wrapNodes(editor, linkElement(url, []), {
      at: { anchor: { path, offset: start }, focus: { path, offset: end } },
      split: true,
    })
  }
}

/** Linkifies every text leaf in the range, e.g. what was just pasted */
export function linkifyRange(editor: Editor, range: Range) {
  const leafPaths = Array.from(Editor.nodes(editor, { at: range, match: Text.isText }), ([, path]) => path)

  // Last first, so wrapping one leaf does not shift the paths of the ones before it
  for (const path of leafPaths.reverse()) {
    linkifyLeaf(editor, path)
  }
}

/** Linkifies the word that ends at the cursor, e.g. before a space or line break goes in */
export function linkifyWordBeforeCursor(editor: Editor) {
  const { selection } = editor
  if (!selection || !Range.isCollapsed(selection)) {
    return
  }
  const { path, offset } = selection.anchor
  const before = Node.leaf(editor, path).text.slice(0, offset)
  const wordStart = before.search(/\S+$/)
  if (wordStart >= 0) {
    linkifyLeaf(editor, path, wordStart, offset)
  }
}
